import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatPacketsPerSecondAxis,
    formatTimeTick,
} from "../../helpers/chart_common";
import {safeBigIntToNumber} from "../../helpers/number_coerce";

export class ShaperSitePackets extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Site Packets";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Site Packets</h5><p>Packets per second.</p>";
    }

    buildContainer() {
        let base = super.buildContainer();
        attachDirectionHint(base);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperSitePacketsGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return ["SiteDetail"];
    }

    onMessage(msg) {
        if (msg.Data.target !== "site_detail") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        for (const [key, value] of Object.entries(msg.Data.blob[0])) {
            this.graph.update(value);
            this.lastGraphState = value;
        }
    }
}

class ShaperSitePacketsGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxis("Packets", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        this.option && this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();

        this.option.series = [];
        this.option.legend = {data: []};

        let n = 0;
        let tcpUp = [];
        let tcpDown = [];
        let udpUp = [];
        let udpDown = [];
        let icmpUp = [];
        let icmpDown = [];

        applySplitDirectionAxes(this.option, {
            ticks: data.body.ticks || [],
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatPacketsPerSecondAxis,
        });

        for (let x = 0; x < data.body.active_circuits.length; x++) {
            tcpDown.push(safeBigIntToNumber(data.body.packets_tcp_down[x]));
            tcpUp.push(0 - safeBigIntToNumber(data.body.packets_tcp_up[x]));
            udpDown.push(safeBigIntToNumber(data.body.packets_udp_down[x]));
            udpUp.push(0 - safeBigIntToNumber(data.body.packets_udp_up[x]));
            icmpDown.push(safeBigIntToNumber(data.body.packets_icmp_down[x]));
            icmpUp.push(0 - safeBigIntToNumber(data.body.packets_icmp_up[x]));
        }

        let seriesTcpDown = {
            type: "line",
            data: tcpDown,
            name: "TCP",
            smooth: true,
            itemStyle: {
                color: window.graphPalette[n]
            },
            areaStyle: {color: window.graphPalette[n]},
            stack: "down",
            xAxisIndex: 0,
            yAxisIndex: 0,
        };
        let seriesTcpUp = {
            type: "line",
            data: tcpUp,
            name: "TCP",
            smooth: true,
            itemStyle: {
                color: window.graphPalette[n]
            },
            areaStyle: {color: window.graphPalette[n]},
            stack: "up",
            xAxisIndex: 1,
            yAxisIndex: 0,
        };

        n++;
        let seriesIcmpDown = {
            type: "line",
            data: icmpDown,
            name: "ICMP",
            smooth: true,
            itemStyle: {
                color: window.graphPalette[n]
            },
            areaStyle: {color: window.graphPalette[n]},
            stack: "down",
            xAxisIndex: 0,
            yAxisIndex: 0,
        };
        let seriesIcmpUp = {
            type: "line",
            data: icmpUp,
            name: "ICMP",
            smooth: true,
            itemStyle: {
                color: window.graphPalette[n]
            },
            areaStyle: {color: window.graphPalette[n]},
            stack: "up",
            xAxisIndex: 1,
            yAxisIndex: 0,
        };

        n++;
        let seriesUdpDown = {
            type: "line",
            data: udpDown,
            name: "UDP",
            smooth: true,
            itemStyle: {
                color: window.graphPalette[n]
            },
            areaStyle: {color: window.graphPalette[n]},
            stack: "down",
            xAxisIndex: 0,
            yAxisIndex: 0,
        };
        let seriesUdpUp = {
            type: "line",
            data: udpUp,
            name: "UDP",
            smooth: true,
            itemStyle: {
                color: window.graphPalette[n]
            },
            areaStyle: {color: window.graphPalette[n]},
            stack: "up",
            xAxisIndex: 1,
            yAxisIndex: 0,
        };

        this.option.series.push(seriesTcpDown);
        this.option.series.push(seriesTcpUp);
        this.option.legend.data.push("TCP");

        this.option.series.push(seriesUdpDown);
        this.option.series.push(seriesUdpUp);
        this.option.legend.data.push("UDP");

        this.option.series.push(seriesIcmpDown);
        this.option.series.push(seriesIcmpUp);
        this.option.legend.data.push("ICMP");

        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatPacketsPerSecondAxis,
            formatter: (params) => {
                const timeIndex = params[0].dataIndex;
                const tcpDownVal = tcpDown[timeIndex] || 0;
                const tcpUpVal = tcpUp[timeIndex] || 0;
                const udpDownVal = udpDown[timeIndex] || 0;
                const udpUpVal = udpUp[timeIndex] || 0;
                const icmpDownVal = icmpDown[timeIndex] || 0;
                const icmpUpVal = icmpUp[timeIndex] || 0;
                return `<div style="font-size: 10px;">
${formatTimeTick(params[0].axisValue)}<br/>
<strong>Download packets</strong><br/>
&nbsp;&nbsp;TCP: ${formatPacketsPerSecondAxis(tcpDownVal)}<br/>
&nbsp;&nbsp;UDP: ${formatPacketsPerSecondAxis(udpDownVal)}<br/>
&nbsp;&nbsp;ICMP: ${formatPacketsPerSecondAxis(icmpDownVal)}<br/>
<strong>Upload packets</strong><br/>
&nbsp;&nbsp;TCP: ${formatPacketsPerSecondAxis(tcpUpVal)}<br/>
&nbsp;&nbsp;UDP: ${formatPacketsPerSecondAxis(udpUpVal)}<br/>
&nbsp;&nbsp;ICMP: ${formatPacketsPerSecondAxis(icmpUpVal)}
</div>`;
            },
        });

        this.option.axisPointer = {
            link: {xAxisIndex: "all"},
            label: {
                backgroundColor: "rgba(30,30,30,0.92)",
                color: "#fff",
                fontFamily: "inherit"
            }
        };

        finalizeSplitDirectionChart(this.option);
        this.chart.setOption(this.option, true);
    }
}
